import { formatIST } from '../../../shared/utils/formatters';
import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Statistic, Button, Table, Tag, Typography, message, Space, Alert } from 'antd'
import { 
  SafetyCertificateOutlined, 
  AlertOutlined, 
  PlayCircleOutlined, 
  AuditOutlined, 
  ReloadOutlined 
} from '@ant-design/icons'
import { walletsService } from '../../../shared/api/services/wallets.service'

const { Title, Text } = Typography

export const ReconciliationStatus: React.FC = () => {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [triggering, setTriggering] = useState<boolean>(false)

  const fetchStatus = async () => {
    try {
      setLoading(true)
      const res = await walletsService.getReconciliationStatus()
      setData(res)
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch reconciliation status')
    } finally {
      setLoading(false)
    }
  }

  const handleTrigger = async () => {
    try {
      setTriggering(true)
      message.loading({ content: 'Running platform ledger audit...', key: 'reconciliation' })
      const res = await walletsService.triggerReconciliation()
      if (res.success) {
        message.success({ content: res.message || 'Ledger audit completed!', key: 'reconciliation' })
        fetchStatus()
      } else {
        message.error({ content: res.message || 'Ledger audit failed', key: 'reconciliation' })
      }
    } catch (error: any) {
      message.error({ content: error.message || 'Ledger audit execution failed', key: 'reconciliation' })
    } finally {
      setTriggering(false)
    }
  }

  useEffect(() => {
    fetchStatus()
  }, [])

  const columns = [
    {
      title: 'Wallet ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Merchant ID',
      dataIndex: 'merchant_id',
      key: 'merchant_id',
      render: (id: number) => <a href={`/merchants/${id}`}>Merchant #{id}</a>
    },
    {
      title: 'Current Balance',
      dataIndex: 'balance',
      key: 'balance',
      render: (balance: number) => <Text type="danger">Rs {balance.toFixed(2)}</Text>
    },
    {
      title: 'Flagged At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date: string) => formatIST(date)
    },
    {
      title: 'Action Required',
      key: 'action',
      render: () => <Tag color="error">RESTRICTED / LEDGER DISCREPANCY</Tag>
    }
  ]

  return (
    <Card 
      title={
        <Space>
          <AuditOutlined style={{ color: '#1890ff' }} />
          <span>Double-Entry Billing & Ledger Reconciliation</span>
        </Space>
      }
      extra={
        <Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchStatus} 
            loading={loading}
            size="small"
          >
            Refresh
          </Button>
          <Button 
            type="primary" 
            icon={<PlayCircleOutlined />} 
            onClick={handleTrigger} 
            loading={triggering}
            size="small"
          >
            Run Audit
          </Button>
        </Space>
      }
      loading={loading && !data}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {data?.discrepant_wallets_count > 0 ? (
          <Alert
            message="CRITICAL INTEGRITY VIOLATION DETECTED"
            description={`${data.discrepant_wallets_count} wallet(s) have sum discrepancy between actual balance and transaction ledger. Bidding privileges for these accounts have been automatically restricted.`}
            type="error"
            showIcon
            icon={<AlertOutlined />}
          />
        ) : (
          <Alert
            message="All Ledger Balances Reconciled & Secure"
            description="Our double-entry accounting engine audited all merchant wallets. Ledger summaries match balances perfectly."
            type="success"
            showIcon
            icon={<SafetyCertificateOutlined />}
          />
        )}

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card type="inner">
              <Statistic
                title="Status"
                value={data?.status === 'healthy' ? 'Healthy' : 'Discrepancy'}
                valueStyle={{ color: data?.status === 'healthy' ? '#52c41a' : '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card type="inner">
              <Statistic
                title="Total Wallets Audited"
                value={data?.total_wallets || 0}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card type="inner">
              <Statistic
                title="Flagged Accounts"
                value={data?.discrepant_wallets_count || 0}
                valueStyle={{ color: data?.discrepant_wallets_count > 0 ? '#ff4d4f' : '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card type="inner">
              <Statistic
                title="Pending Recharges"
                value={data?.pending_recharges_count || 0}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
        </Row>

        {data?.discrepant_wallets_count > 0 && (
          <div>
            <Title level={5} type="danger" style={{ marginBottom: '12px' }}>
              Restricted Accounts & Audit Ledger Discrepancies
            </Title>
            <Table 
              dataSource={data?.discrepancies || []} 
              columns={columns} 
              rowKey="id" 
              pagination={false}
              size="small"
              bordered
            />
          </div>
        )}
      </Space>
    </Card>
  )
}
